import React from "react";
import Badge from "./Badge";
import Avatar from "./Avatar";
import Button from "./Button";
import FormSelect from "./FormSelect";
import { MapPin, Calendar, User, ShieldCheck, Phone, DollarSign, Lock } from "lucide-react";

/**
 * Unified Kanban Card for both Leads and Contacts
 */
export const UnifiedKanbanCard = ({ 
  item, 
  onClick, 
  onUpdate, 
  teamMembers = [],
  variant = "lead" // "lead" or "contact"
}) => {
  const isLead = variant === "lead";
  const isDisabled = item.isDisabled;
  const isLocked = item.isLocked;
  
  // Formatters
  const fmtCurrency = (v) => new Intl.NumberFormat("en-IN", { 
    style: 'currency', 
    currency: 'INR', 
    maximumSignificantDigits: 3 
  }).format(v);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { 
    day: "2-digit", 
    month: "short" 
  }) : "—";

  // Data Normalization
  const id = (item.customId || item.customID || "").toUpperCase() || (isLead ? "NEW LEAD" : "NEW CONTACT");
  const name = item.customerName || `${item.firstName || ""} ${item.lastName || ""}`.trim() || "No Name";
  const location = item.city || item.location || (isLead ? "Location not set" : "No location");
  const date = item.createdAt || item.date;
  const source = item.source || item.referenceType || "Manual";

  // Role Filtering for Dropdowns
  const salesExecs = (teamMembers || []).filter(u => {
    const role = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    return ["sales-exec", "sale-exec", "sales-head", "sale-head", "sales"].includes(role);
  });
  
  const projectManagers = (teamMembers || []).filter(u => {
    const role = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    return ["project-manager", "pm"].includes(role);
  });

  const currentAssignedId = item.assignedTo?._id || item.assignedTo || "";
  const currentPMId = item.projectManager?._id || item.projectManager || "";
  const isPMDisabled = !['project_started', 'approved', 'site_visit'].includes(item.status);

  return (
    <div
      onClick={() => onClick(item)}
      className={`p-4 ${variant === 'account' ? 'min-h-[210px]' : 'h-[210px]'} flex flex-col transition-all bg-white shadow-sm hover:shadow-md rounded-lg cursor-pointer group overflow-hidden border border-slate-300 border-l-[4px] ${
        isLead ? 'border-l-brand-orange' : ''
      } ${isDisabled ? 'opacity-50 grayscale bg-slate-50/50' : ''}`}
    >
      {/* Top Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <div className={`font-black text-[9px] uppercase tracking-wider ${isLead ? 'text-brand-orange' : 'text-brand-blue'}`}>
              {id}
            </div>
            {isLocked && <Lock size={10} className="text-slate-300" />}
          </div>
          <div className="font-bold text-[13px] text-brand-dark group-hover:text-brand-blue transition-colors leading-tight truncate">
            {name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {item.serviceType && <Badge status={item.serviceType} className="scale-90 origin-right" />}
          {item.priority && (
             <Badge
               status={item.priority === "high" ? "Hot" : item.priority === "medium" ? "Warm" : "Cold"}
               custom={item.priority === "high" ? "bg-red-50 text-red-600" : item.priority === "medium" ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"}
               className="scale-[0.8] origin-right -mt-1 font-bold uppercase tracking-tighter"
             />
          )}
        </div>
      </div>
      
      {/* Middle Content */}
      <div className="space-y-2.5 mb-4 flex-1">
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5 font-medium">
          <MapPin size={12} className="text-slate-300 shrink-0" />
          <span className="truncate">{location}</span>
          {item.callCount > 0 && (
            <span className="text-[9px] bg-blue-50 px-1.5 py-0.5 rounded-full text-brand-blue font-bold flex items-center gap-1 shrink-0 ml-auto border border-blue-100/50">
              <Phone size={8} /> {item.callCount}
            </span>
          )}
        </div>
        
        {variant === "account" ? (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="flex flex-col p-1.5 bg-slate-50 rounded border border-slate-100">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Total Value</span>
              <span className="text-[11px] font-bold text-slate-700 mt-0.5">{fmtCurrency(item.budget || item.projectValue || 0)}</span>
            </div>
            <div className="flex flex-col p-1.5 bg-emerald-50 rounded border border-emerald-100/50">
              <span className="text-[8px] font-bold text-emerald-600/70 uppercase tracking-wider">Paid by Customer</span>
              <span className="text-[11px] font-bold text-emerald-700 mt-0.5">{fmtCurrency(item.totalReceived || 0)}</span>
            </div>
            <div className="flex flex-col p-1.5 bg-amber-50 rounded border border-amber-100/50">
              <span className="text-[8px] font-bold text-amber-600/70 uppercase tracking-wider">Paid to Customer</span>
              <span className="text-[11px] font-bold text-amber-700 mt-0.5">{fmtCurrency(item.advanceToCustomer || item.advance || 0)}</span>
            </div>
            <div className="flex flex-col p-1.5 bg-rose-50 rounded border border-rose-100/50">
              <span className="text-[8px] font-bold text-rose-600/70 uppercase tracking-wider">Balance</span>
              <span className="text-[11px] font-bold text-rose-700 mt-0.5">{fmtCurrency(item.balance || 0)}</span>
            </div>
          </div>
        ) : item.budget > 0 ? (
          <div className="text-[10px] font-bold text-emerald-700 bg-emerald-50 w-fit px-2 py-0.5 rounded border border-emerald-100/50 flex items-center gap-1">
            <DollarSign size={10} /> {fmtCurrency(item.budget)}
          </div>
        ) : null}
      </div>
      
      {/* Metadata Strip */}
      <div className="flex items-center justify-between py-2 border-y border-slate-50 bg-slate-50/30 -mx-4 px-4">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{source}</span>
        <div className="flex items-center gap-1 text-[9px] text-slate-400 font-bold">
          <Calendar size={10} />
          {fmtDate(date)}
        </div>
      </div>

      {isLead && (
        <div className="grid grid-cols-2 gap-2 border-slate-50">
          <FormSelect
            size="sm"
            value={currentAssignedId}
            disabled={!!currentAssignedId}
            onClick={e => e.stopPropagation()}
            onChange={e => onUpdate(item._id || item.id, { assignedTo: e.target.value, status: e.target.value ? 'assigned' : item.status })}
            options={salesExecs}
            placeholder="Sales Exec"
            inputClassName="text-[10px] font-bold text-slate-600 h-6"
          />

          <FormSelect
            size="sm"
            value={currentPMId}
            disabled={isPMDisabled || !!currentPMId}
            onClick={e => e.stopPropagation()}
            onChange={e => onUpdate(item._id || item.id, { projectManager: e.target.value })}
            options={projectManagers}
            placeholder="Assign PM"
            inputClassName={`text-[10px] font-bold h-6 ${isPMDisabled || !!currentPMId ? 'text-slate-400' : 'text-emerald-700'}`}
          />
        </div>
      )}
    </div>
  );
};

export default UnifiedKanbanCard;
